import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  clearHistory,
  type SnackbarEntry,
} from '@/store/slices/snackbarSlice';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * Floating debug-only error inspector.
 *
 * Visible only to users with the `toBeOver9000` permission. Shows a small
 * circular icon at the top-right with a badge count of recorded API errors.
 * Clicking the icon expands a panel listing recent errors with copy + clear
 * actions. The transient toast for individual errors is still rendered by
 * `Snackbar`.
 */

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function formatApiLabel(entry: SnackbarEntry): string {
  if (!entry.apiLabel) return '';
  return `[${entry.method ? `${entry.method} ` : ''}${entry.apiLabel}]`;
}

function toClipboardText(entry: SnackbarEntry): string {
  const meta = [
    entry.code != null ? `HTTP ${entry.code}` : null,
    entry.method,
    entry.apiLabel,
    timeFormatter.format(new Date(entry.timestamp)),
  ]
    .filter(Boolean)
    .join(' | ');
  const parts = [`${meta}\n${entry.text}`.trim()];
  if (entry.detail) parts.push(`\n--- Raw Response ---\n${entry.detail}`);
  return parts.join('');
}

export default function ErrorBadge() {
  const { t } = useI18nText();
  const dispatch = useAppDispatch();
  const isDebugUser = useAppSelector((s) =>
    Boolean(s.envs.currentUser?.permissions?.toBeOver9000),
  );
  const current = useAppSelector((s) => s.snackbar.current);
  const history = useAppSelector((s) => s.snackbar.history);
  const [expanded, setExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!expanded) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || !rootRef.current) return;
      if (!rootRef.current.contains(target)) setExpanded(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [expanded]);

  const apiErrors = useMemo(() => {
    const list: SnackbarEntry[] = [];
    if (current?.kind === 'api-error') list.push(current);
    for (const e of history) {
      if (e.kind === 'api-error' && e.id !== current?.id) list.push(e);
    }
    return list;
  }, [current, history]);

  if (!isDebugUser || apiErrors.length === 0) return null;

  const handleCopy = async (entry: SnackbarEntry) => {
    try {
      await navigator.clipboard.writeText(toClipboardText(entry));
      setCopiedId(entry.id);
      window.setTimeout(() => setCopiedId(null), 1500);
    } catch {
      // ignored — clipboard may not be available
    }
  };

  return (
    <div ref={rootRef} className="fixed right-4 top-3 z-[9999] font-ggc">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={t('Debug errors', '디버그 에러')}
        className="relative inline-flex size-9 items-center justify-center rounded-ggc-pill bg-ggc-main text-white shadow-ggc-md transition-opacity hover:opacity-90"
      >
        <i aria-hidden className="fa fa-exclamation-circle text-[16px]" />
        <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-ggc-pill bg-ggc-red px-[5px] leading-none text-white ggc-text-badge">
          {apiErrors.length}
        </span>
      </button>

      {expanded && (
        <div className="absolute right-0 top-full mt-2 w-[420px] max-w-[90vw] overflow-hidden rounded-ggc-lg border border-ggc-border-gray bg-white shadow-ggc-lg">
          <div className="flex items-center justify-between border-b border-ggc-border-gray bg-ggc-bg-base px-4 py-2">
            <div className="flex items-center gap-2 text-ggc-text-primary">
              <i aria-hidden className="fa fa-bug text-ggc-text-gray" />
              <span className="ggc-text-body-bold">
                {t('Recent API errors', '최근 API 에러')}
              </span>
              <span className="ggc-text-caption-s text-ggc-text-gray">
                ({apiErrors.length})
              </span>
            </div>
            <button
              type="button"
              onClick={() => dispatch(clearHistory())}
              className="rounded-ggc-sm px-2 py-1 ggc-text-caption-m text-ggc-text-gray transition-colors hover:bg-white hover:text-ggc-text-primary"
            >
              {t('Clear all', '전체 비우기')}
            </button>
          </div>
          <div className="flex items-start gap-2 border-b border-ggc-border-gray bg-ggc-sub-yellow-light px-4 py-2 leading-snug text-ggc-sub-amber ggc-text-caption-s">
            <i aria-hidden className="fa fa-info-circle mt-[1px] ggc-text-body" />
            <span>
              {t(
                'Developer-only debug panel. Use the copy actions to share error details with the team.',
                '개발자 디버깅용 패널입니다. Copy 버튼으로 에러 상세를 팀과 공유하세요.',
              )}
            </span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {apiErrors.map((entry) => (
              <div
                key={entry.id}
                className="border-b border-ggc-border-gray px-4 py-3 last:border-b-0"
              >
                <div className="mb-1 flex items-center gap-2 ggc-text-caption-s text-ggc-text-gray">
                  {entry.code != null && (
                    <span className="inline-flex items-center rounded-ggc-sm bg-ggc-red-light px-[6px] py-[2px] text-ggc-red ggc-text-badge">
                      {entry.code}
                    </span>
                  )}
                  {entry.apiLabel && (
                    <span className="font-mono text-ggc-text-primary">
                      {formatApiLabel(entry)}
                    </span>
                  )}
                  <span className="ml-auto">
                    {timeFormatter.format(new Date(entry.timestamp))}
                  </span>
                </div>
                <p className="break-words text-[12px] text-ggc-text-primary">
                  {entry.text}
                </p>
                {entry.detail && (
                  <pre className="mt-1 max-h-[120px] overflow-auto rounded-ggc-sm bg-ggc-bg-base p-2 ggc-text-caption-s text-ggc-text-gray whitespace-pre-wrap break-all">
                    {entry.detail}
                  </pre>
                )}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => void handleCopy(entry)}
                    className="rounded-ggc-sm border border-ggc-border-gray-dark bg-white px-2 py-1 text-[10px] font-semibold text-ggc-text-primary transition-colors hover:bg-ggc-bg-surface"
                  >
                    {copiedId === entry.id
                      ? t('Copied', '복사됨')
                      : t('Copy', '복사')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
